import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { PhoneCall, Home, FileText, Upload, TrendingUp, LogOut, Users, Settings, Activity, ClipboardCheck, UserCircle, Shield, CreditCard, Building2 } from 'lucide-react';
import NotificationBell from './NotificationBell';
import CreditBalance from './CreditBalance';
import WorkspaceSwitcher from './WorkspaceSwitcher';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const permissions = user?.permissions || [];
  const isAgent = user?.role === 'agent';

  const navigation = isAgent
    ? [{ name: 'My Scorecard', path: '/dashboard', icon: UserCircle }]
    : [
        { name: 'Dashboard', path: '/dashboard', icon: Home },
        { name: 'Live Monitor', path: '/live-monitor', icon: Activity },
        { name: 'SOPs', path: '/sops', icon: FileText, permission: 'manage_sops' },
        { name: 'Audit Sheets', path: '/audit-sheets', icon: ClipboardCheck, permission: 'manage_sops' },
        { name: 'Agents', path: '/agents', icon: UserCircle, permission: 'manage_agents' },
        { name: 'Users', path: '/user-management', icon: Users, permission: 'manage_users' },
        { name: 'Upload Call', path: '/upload', icon: Upload, permission: 'upload_calls' },
        { name: 'Bulk Upload', path: '/bulk-upload', icon: Upload, permission: 'upload_calls' },
        { name: 'Teams', path: '/teams', icon: Users, permission: 'manage_teams' },
        { name: 'Reports', path: '/reports', icon: TrendingUp, permission: 'view_reports' },
        { name: 'Analytics', path: '/analytics', icon: TrendingUp, permission: 'view_reports' },
        { name: 'Credits', path: '/credits', icon: CreditCard },
        { name: 'Integrations', path: '/integrations', icon: Settings, permission: 'manage_integrations' },
      ];

  // Add Super Admin / Companies nav items if user is the platform owner
  const superAdminNav = user?.is_super_admin ? [
    { name: 'Companies', path: '/companies', icon: Building2 },
    { name: 'Super Admin', path: '/super-admin', icon: Shield }
  ] : [];

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNav = [
    ...superAdminNav,
    ...navigation.filter((item) => !item.permission || permissions.includes(item.permission)),
  ];

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-slate-200 flex flex-col" data-testid="sidebar">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <PhoneCall size={32} className="text-[#4338CA]" />
            <div>
              <h1
                className="text-xl font-bold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                CallScript AI
              </h1>
              <p className="text-xs text-slate-600">
                {user?.is_super_admin ? 'Platform Owner' : (user?.role === 'custom' ? 'Custom Role' : user?.role)}
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNav.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  active
                    ? (item.name === 'Super Admin' || item.name === 'Companies')
                      ? 'bg-purple-50 text-purple-700 font-medium'
                      : 'bg-indigo-50 text-[#4338CA] font-medium'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                <Icon size={20} strokeWidth={active ? 2 : 1.5} className={(item.name === 'Super Admin' || item.name === 'Companies') && active ? 'text-purple-600' : ''} />
                <span>{item.name}</span>
              </button>
            );
          })}
        </nav>

        {/* Credit Balance */}
        {user?.subscription_id && (
          <div className="px-4 pb-2">
            <CreditBalance showPurchase={false} />
          </div>
        )}

        <div className="p-4 border-t border-slate-200">
          <div className="mb-3 px-2">
            <p className="text-sm font-medium text-slate-900">{user?.name}</p>
            <p className="text-xs text-slate-600">{user?.email}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="logout-button"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Top Bar with Notification */}
        <div className="bg-white border-b border-slate-200 px-6 py-3 flex justify-between items-center sticky top-0 z-40">
          <WorkspaceSwitcher />
          <div className="flex items-center gap-4">
            <NotificationBell />
            <span className="text-sm text-slate-600">Welcome back!</span>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-8">{children}</div>
      </div>
    </div>
  );
};

export default Layout;